import { useMemo, useState } from "react";
import { Search, Filter, X } from "lucide-react";

/**
 * Standard products filter bar, shared across the app so search + filtering
 * looks and behaves identically everywhere. Controlled: the parent owns
 * `search` and `filters` and applies the filtering client-side, so the table
 * updates in REAL TIME.
 *
 * When `extended` is true it exposes the SAME fields as the Prodotti page:
 * Brand, Categoria, Sotto-categoria, Codice, Codice a barre, Ingrediente INCI,
 * Codice CAS, % minima ingrediente, Prezzo (min/max).
 */
export const EMPTY_PRODUCT_FILTERS = {
  brand_id: "", category_id: "", subcategory_id: "",
  code: "", barcode: "", inci: "", cas: "", inci_min_percent: "",
  price_min: "", price_max: "",
};

export default function ProductFilterBar({
  search, onSearchChange,
  filters, onFiltersChange,
  brands = [], cats = [],
  count, total,
  extended = false,
  searchPlaceholder = "Cerca per nome, codice...",
}) {
  const [showFilters, setShowFilters] = useState(false);
  const brandMap = useMemo(() => Object.fromEntries(brands.map(b => [b.id, b])), [brands]);
  const catMap = useMemo(() => Object.fromEntries(cats.map(c => [c.id, c])), [cats]);
  const topCats = useMemo(() => cats.filter(c => !c.parent_id), [cats]);
  const subcats = useMemo(
    () => cats.filter(c => c.parent_id && (!filters.category_id || c.parent_id === filters.category_id)),
    [cats, filters.category_id]);

  const set = (patch) => onFiltersChange({ ...filters, ...patch });
  const clearAll = () => onFiltersChange({ ...EMPTY_PRODUCT_FILTERS });

  const chips = [];
  if (filters.brand_id) chips.push({ key: "brand_id", label: `Brand: ${brandMap[filters.brand_id]?.name || filters.brand_id}` });
  if (filters.category_id) chips.push({ key: "category_id", label: `Categoria: ${catMap[filters.category_id]?.name || filters.category_id}` });
  if (filters.subcategory_id) chips.push({ key: "subcategory_id", label: `Sotto-categoria: ${catMap[filters.subcategory_id]?.name || filters.subcategory_id}` });
  if (filters.code) chips.push({ key: "code", label: `Codice: ${filters.code}` });
  if (filters.barcode) chips.push({ key: "barcode", label: `Barcode: ${filters.barcode}` });
  if (filters.inci) chips.push({ key: "inci", label: `INCI: ${filters.inci}` });
  if (filters.cas) chips.push({ key: "cas", label: `CAS: ${filters.cas}` });
  if (filters.inci_min_percent) chips.push({ key: "inci_min_percent", label: `% min: ${filters.inci_min_percent}` });
  if (filters.price_min !== "" || filters.price_max !== "") chips.push({ key: "price", label: `Prezzo: ${filters.price_min !== "" ? filters.price_min : "0"}–${filters.price_max !== "" ? filters.price_max : "∞"}` });

  const removeChip = (key) => {
    if (key === "category_id") set({ category_id: "", subcategory_id: "" });
    else if (key === "price") set({ price_min: "", price_max: "" });
    else set({ [key]: "" });
  };

  return (
    <div className="space-y-3" data-testid="product-filter-bar">
      <div className="relative">
      <div className="flex items-stretch gap-3">
        <div className="luxury-card px-4 py-2.5 flex items-center gap-3 flex-1 min-w-0">
          <Search className="w-4 h-4 text-[#707070]" strokeWidth={1.5} />
          <input data-testid="pfb-search-input" placeholder={searchPlaceholder}
                 className="flex-1 min-w-0 outline-none bg-transparent text-sm"
                 value={search} onChange={(e) => onSearchChange(e.target.value)} />
        </div>
        <button onClick={() => setShowFilters(s => !s)}
                className={`btn-ghost flex items-center shrink-0 ${showFilters ? "bg-[#1A1A1A] text-[#FAF9F6] border-[#1A1A1A]" : (chips.length ? "!border-[#B45309] !text-[#B45309]" : "")}`}
                data-testid="pfb-toggle-filters-btn">
          <Filter className="w-3 h-3 mr-2" strokeWidth={1.5} />
          {showFilters ? "Chiudi filtri" : (chips.length ? `Filtri (${chips.length})` : "Filtri")}
        </button>
      </div>

      {showFilters && (
        <div className="luxury-card p-3 relative" data-testid="pfb-panel">
          <button onClick={() => setShowFilters(false)}
                  className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-sm text-[#707070] hover:text-[#1A1A1A] hover:bg-[#F4F4F0] transition text-lg leading-none"
                  aria-label="Chiudi filtri" data-testid="pfb-close-btn">×</button>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-2 [&_.input-luxury]:!py-1.5 [&_.input-luxury]:!text-sm [&_.label-tiny]:!mb-0.5">
            <div>
              <label className="label-tiny">Brand</label>
              <select className={`input-luxury ${!filters.brand_id ? "product-ux-select-empty" : ""}`} value={filters.brand_id} data-testid="pfb-filter-brand"
                      onChange={(e) => set({ brand_id: e.target.value })}>
                <option value="">Tutti</option>
                {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label-tiny">Categoria</label>
              <select className={`input-luxury ${!filters.category_id ? "product-ux-select-empty" : ""}`} value={filters.category_id} data-testid="pfb-filter-category"
                      onChange={(e) => set({ category_id: e.target.value, subcategory_id: "" })}>
                <option value="">Tutte</option>
                {topCats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label-tiny">Sotto-categoria</label>
              <select className={`input-luxury ${!filters.subcategory_id ? "product-ux-select-empty" : ""}`} value={filters.subcategory_id} data-testid="pfb-filter-subcategory"
                      disabled={!filters.category_id}
                      onChange={(e) => set({ subcategory_id: e.target.value })}>
                <option value="">{filters.category_id ? "Tutte" : "Seleziona prima una categoria"}</option>
                {subcats.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>

            {extended && (<>
              <div>
                <label className="label-tiny">Codice prodotto</label>
                <input className="input-luxury" data-testid="pfb-filter-code" value={filters.code} placeholder="es. PRD-001"
                       onChange={(e) => set({ code: e.target.value })} />
              </div>
              <div>
                <label className="label-tiny">Codice a barre</label>
                <input className="input-luxury" data-testid="pfb-filter-barcode" value={filters.barcode} placeholder="es. 8001234567890"
                       onChange={(e) => set({ barcode: e.target.value })} />
              </div>
              <div>
                <label className="label-tiny">Ingrediente INCI</label>
                <input className="input-luxury" data-testid="pfb-filter-inci" value={filters.inci} placeholder="Nome ingrediente"
                       onChange={(e) => set({ inci: e.target.value })} />
              </div>
              <div>
                <label className="label-tiny">Codice CAS</label>
                <input className="input-luxury" data-testid="pfb-filter-cas" value={filters.cas} placeholder="es. 7732-18-5"
                       onChange={(e) => set({ cas: e.target.value })} />
              </div>
              <div>
                <label className="label-tiny">% minima ingrediente</label>
                <input className="input-luxury" type="number" step="0.1" data-testid="pfb-filter-inci-percent" placeholder="es. 5"
                       value={filters.inci_min_percent}
                       onChange={(e) => set({ inci_min_percent: e.target.value })} />
              </div>
              <div>
                <label className="label-tiny">Prezzo (min / max)</label>
                <div className="flex items-center gap-2">
                  <input className="input-luxury" type="number" step="0.01" placeholder="min" data-testid="pfb-filter-price-min"
                         value={filters.price_min}
                         onChange={(e) => set({ price_min: e.target.value })} />
                  <span className="text-[#C5B39B]">–</span>
                  <input className="input-luxury" type="number" step="0.01" placeholder="max" data-testid="pfb-filter-price-max"
                         value={filters.price_max}
                         onChange={(e) => set({ price_max: e.target.value })} />
                </div>
              </div>
            </>)}
          </div>
          <div className="flex justify-end gap-3 mt-3">
            <button onClick={clearAll} className="btn-ghost" data-testid="pfb-clear-btn">Pulisci</button>
            <button onClick={() => setShowFilters(false)} className="btn-primary" data-testid="pfb-apply-btn">Applica</button>
          </div>
        </div>
      )}
      </div>

      {chips.length > 0 && (
        <div className="flex items-center flex-wrap gap-2" data-testid="pfb-active-filters">
          <span className="text-[10px] tracking-luxury uppercase text-[#707070] mr-1">Filtri attivi:</span>
          {chips.map(c => (
            <button key={c.key} onClick={() => removeChip(c.key)}
                    className="inline-flex items-center gap-1.5 text-xs pl-3 pr-2 py-1 bg-[#F4F4F0] border border-[#E5E5E0] rounded-full hover:border-[#C5B39B] transition"
                    data-testid={`pfb-active-filter-${c.key}`}>
              {c.label}
              <X className="w-3 h-3 text-[#707070]" strokeWidth={2} />
            </button>
          ))}
          <button onClick={clearAll}
                  className="text-[10px] tracking-luxury uppercase text-[#8C3A3A] hover:opacity-70 ml-1"
                  data-testid="pfb-active-filters-clear-all">Pulisci tutto</button>
          {typeof count === "number" && typeof total === "number" && (
            <span className="text-xs text-[#707070] ml-2">{count} di {total}</span>
          )}
        </div>
      )}
    </div>
  );
}
